import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, LogOut, Plus } from "lucide-react";
import { hardcodedGalleryImages, ALL_CATEGORIES, type GalleryCategory, type GalleryImageData } from "@/data/galleryImages";
import ImageCard, { type DbGalleryImage } from "@/components/admin/ImageCard";
import AdminContractorsTab from "@/components/admin/AdminContractorsTab";
import AdminProjectsTab from "@/components/admin/AdminProjectsTab";
import AdminQuoteEstimatorTab from "@/components/admin/AdminQuoteEstimatorTab";
import AdminPricingTab from "@/components/admin/AdminPricingTab";
const AdminDashboard = () => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [dbImages, setDbImages] = useState<DbGalleryImage[]>([]);
  const [hiddenSrcs, setHiddenSrcs] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [alt, setAlt] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<GalleryCategory[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [activeCategory, setActiveCategory] = useState<GalleryCategory | "All">("All");
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) {
      navigate("/admin/login", { replace: true });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    const [galleryRes, hiddenRes] = await Promise.all([
      supabase.from("gallery_images").select("*").order("sort_order", { ascending: true }),
      supabase.from("hidden_gallery_sources").select("src"),
    ]);
    if (galleryRes.error) {
      toast.error("Failed to load gallery images");
      return;
    }
    if (galleryRes.data) setDbImages(galleryRes.data.map((img: any) => ({ ...img, isHardcoded: false })));
    if (hiddenRes.data) setHiddenSrcs(new Set(hiddenRes.data.map((r: any) => r.src)));
  };

  const toggleCategory = (cat: GalleryCategory) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0 || selectedCategories.length === 0) {
      toast.error("Please select file(s) and at least one category");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    // Compute next sort_order from server to avoid stale client state
    const { data: maxRow } = await supabase
      .from("gallery_images")
      .select("sort_order")
      .order("sort_order", { ascending: false })
      .limit(1)
      .maybeSingle();
    const baseSortOrder = (maxRow?.sort_order ?? -1) + 1;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(`Uploading ${i + 1} of ${files.length}...`);
      try {
        const ext = file.name.split(".").pop();
        const fileName = `${crypto.randomUUID()}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from("gallery")
          .upload(fileName, file);
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("gallery")
          .getPublicUrl(fileName);

        const { error: insertError } = await supabase
          .from("gallery_images")
          .insert({
            src: urlData.publicUrl,
            alt: files.length === 1 && alt ? alt : file.name,
            categories: selectedCategories,
            sort_order: baseSortOrder + i,
          });
        if (insertError) throw insertError;
        successCount++;
      } catch (err: any) {
        console.error(`Failed to upload ${file.name}:`, err);
        failCount++;
      }
    }

    setUploading(false);
    setUploadProgress("");
    setFiles([]);
    setAlt("");
    setSelectedCategories([]);
    const fileInput = document.getElementById("file-input") as HTMLInputElement;
    if (fileInput) fileInput.value = "";
    fetchImages();

    if (failCount === 0) {
      toast.success(`${successCount} image${successCount > 1 ? "s" : ""} uploaded!`);
    } else {
      toast.error(`${successCount} uploaded, ${failCount} failed`);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // Combine all images, deduplicating hardcoded ones that have been saved to DB,
  // and excluding built-in images the admin has hidden.
  const dbSrcs = new Set(dbImages.map(img => img.src));
  const allImages: (GalleryImageData | DbGalleryImage)[] = [
    ...hardcodedGalleryImages
      .filter(img => !dbSrcs.has(img.src) && !hiddenSrcs.has(img.src))
      .map(img => ({ ...img, isHardcoded: true })),
    ...dbImages,
  ];


  const getImagesForCategory = (cat: GalleryCategory) =>
    allImages.filter((img) => img.categories.includes(cat));

  const categoriesToShow = activeCategory === "All" ? ALL_CATEGORIES : [activeCategory];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Cabinet Shop Admin
        </h1>
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => navigate("/contractor/dashboard")}>
            Contractor View
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/")}>
            View Site
          </Button>
          <Button variant="outline" size="sm" onClick={handleSignOut}>
            <LogOut className="w-4 h-4" /> Sign Out
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
        <Tabs defaultValue="quote-estimator" className="w-full">
          <div className="mb-8">
            <TabsList className="h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
              <TabsTrigger value="quote-estimator" className="min-h-12 border-2 border-border px-4 text-base font-bold text-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Cabinet Quoting</TabsTrigger>
              <TabsTrigger value="projects" className="min-h-12 border-2 border-border px-4 text-base font-semibold text-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All Projects</TabsTrigger>
              <TabsTrigger value="pricing" className="min-h-12 border-2 border-border px-4 text-base font-semibold text-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Pricing</TabsTrigger>
              <TabsTrigger value="contractors" className="min-h-12 border-2 border-border px-4 text-base font-semibold text-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Contractors</TabsTrigger>
              <TabsTrigger value="gallery" className="min-h-12 border-2 border-border px-4 text-base font-semibold text-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Gallery</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="gallery">
            {/* Upload Form */}
            <div className="border border-border p-4 sm:p-6 mb-8 sm:mb-10">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5" /> Add New Image
              </h2>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">
                    Image File(s)
                  </Label>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      const droppedFiles = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/"));
                      if (droppedFiles.length > 0) {
                        setFiles(prev => [...prev, ...droppedFiles]);
                      } else {
                        toast.error("Please drop image files");
                      }
                    }}
                    onClick={() => document.getElementById("file-input")?.click()}
                    className={`mt-1 border-2 border-dashed rounded-md p-5 sm:p-8 text-center cursor-pointer transition-colors ${
                      dragOver
                        ? "border-primary bg-primary/5"
                        : files.length > 0
                        ? "border-primary/50 bg-primary/5"
                        : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <input
                      id="file-input"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const selected = Array.from(e.target.files || []).filter(f => f.type.startsWith("image/"));
                        if (selected.length > 0) setFiles(prev => [...prev, ...selected]);
                      }}
                    />
                    {files.length > 0 ? (
                      <div className="space-y-1">
                        <p className="text-sm text-foreground font-medium">{files.length} file{files.length > 1 ? "s" : ""} selected</p>
                        <p className="text-xs text-muted-foreground">{files.map(f => f.name).join(", ")}</p>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setFiles([]); }} className="text-xs text-destructive underline mt-1">Clear</button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Drag & drop images here, or <span className="underline">click to browse</span>
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="alt-text" className="text-xs tracking-[0.15em] uppercase text-muted-foreground">
                    Description {files.length > 1 && <span className="normal-case tracking-normal">(applies to single uploads only)</span>}
                  </Label>
                  <Input
                    id="alt-text"
                    value={alt}
                    onChange={(e) => setAlt(e.target.value)}
                    placeholder="e.g. White kitchen with marble countertops"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                    Categories
                  </Label>
                  <div className="flex flex-wrap gap-4">
                    {ALL_CATEGORIES.map((cat) => (
                      <label key={cat} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={selectedCategories.includes(cat)}
                          onCheckedChange={() => toggleCategory(cat)}
                        />
                        <span className="text-sm text-foreground">{cat}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={uploading}>
                  {uploading ? (uploadProgress || "Uploading...") : `Upload ${files.length > 1 ? `${files.length} Images` : "Image"}`}
                </Button>
              </form>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {(["All", ...ALL_CATEGORIES] as const).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 text-xs tracking-[0.15em] uppercase transition-all duration-300 ${
                    activeCategory === cat
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground border border-border"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Images by Category */}
            {categoriesToShow.map((cat) => {
              const catImages = getImagesForCategory(cat);
              return (
                <div key={cat} className="mb-10">
                  <h2 className="text-lg font-medium text-foreground mb-4">
                    {cat} ({catImages.length})
                  </h2>
                  {catImages.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No images in this category.</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {catImages.map((image, idx) => (
                        <ImageCard
                          key={image.src + idx}
                          image={image}
                          onDeleted={fetchImages}
                          onUpdated={fetchImages}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="contractors">
            <AdminContractorsTab />
          </TabsContent>

          <TabsContent value="projects">
            <AdminProjectsTab />
          </TabsContent>

          <TabsContent value="quote-estimator">
            <AdminQuoteEstimatorTab />
          </TabsContent>

          <TabsContent value="pricing">
            <AdminPricingTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
